import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ConfirmProfile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("Kein Bestätigungs-Token gefunden");
      return;
    }

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token: string) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        "verify-profile-confirmation",
        {
          body: { token },
        }
      );

      if (error) throw error;

      if (data.success) {
        setStatus("success");
        setMessage("Dein Profil wurde erfolgreich bestätigt!");
        toast({
          title: "Profil bestätigt",
          description: "Dein Profil ist jetzt aktiv.",
        });
      } else {
        throw new Error(data.error || "Bestätigung fehlgeschlagen");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Der Bestätigungs-Link ist ungültig oder abgelaufen"
      );
      toast({
        title: "Fehler",
        description: "Die Bestätigung ist fehlgeschlagen",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === "loading" && (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
            {status === "success" && (
              <CheckCircle className="h-16 w-16 text-green-500" />
            )}
            {status === "error" && (
              <XCircle className="h-16 w-16 text-destructive" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === "loading" && "Profil wird bestätigt..."}
            {status === "success" && "Profil bestätigt!"}
            {status === "error" && "Bestätigung fehlgeschlagen"}
          </CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "success" && (
            <Button onClick={() => navigate("/")} className="w-full">
              Zur Startseite
            </Button>
          )}
          {status === "error" && (
            <div className="space-y-2">
              <Button onClick={() => navigate("/")} className="w-full">
                Zur Startseite
              </Button>
              <Button
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="w-full"
              >
                Premium upgraden
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmProfile;
